/**
 * Simulation Detail Page
 * Shows simulation status, results, and visualizations.
 */
import React, { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import simulationService from '../../services/simulationService';
import { api } from '../../services/authService';
import StatusIcon, { getStatusColorClass } from '../../components/StatusIcon';
import SimulationTimeline from '../../components/SimulationTimeline';
import JournauxCalcul from '../../components/JournauxCalcul';
import useCaseCatalog, { findCase, estimatedCostFromCatalog } from '../../hooks/useCaseCatalog';
import { useTranslation } from 'react-i18next';
import DiagnosticSimulation from '../../components/DiagnosticSimulation';
import useAuthStore from '../../stores/authStore';

// Lazy load the 3D viewers to avoid loading Three.js/vtk.js unnecessarily
const WingViewer3D = lazy(() => import('../../components/dafoam/WingViewer3D'));
const WingViewerVTK = lazy(() => import('../../components/dafoam/WingViewerVTK'));

// Cost estimates for the legacy workflows (displayed before completion).
// Manifest-driven cases declare theirs per variant (variants[].cost_usd) and are
// resolved from the case catalog, so they are deliberately absent here.
const COST_ESTIMATES = {
  mach_wing: {
    standard: 0.31,
    fast: 0.15,
    ultrafast: 0.05,
    aero_only: 0.20
  },
  drone_wing: {
    empirical: 0.001,
    cfd: 0.02
  }
};

/**
 * Get estimated cost for a simulation based on workflow and mode.
 * Used as fallback when actual cost is not available.
 * `cases` is the catalog (may be empty while it loads).
 */
const getEstimatedCost = (sim, cases) => {
  if (!sim) return '0.10';
  const fromCatalog = estimatedCostFromCatalog(cases, sim.workflow_type, sim.analysis_mode);
  if (fromCatalog !== null) return fromCatalog.toFixed(4);
  const workflowEstimates = COST_ESTIMATES[sim.workflow_type];
  if (!workflowEstimates) return '0.10';
  return (workflowEstimates[sim.analysis_mode] || 0.10).toFixed(4);
};

/**
 * Extract CFD metrics from results data.
 * Handles double nesting issue: DLR F6 / Custom Geometry have results.results.results structure
 * while other workflows may have results.results structure.
 */
const getCfdMetrics = (resultsData) => {
  if (!resultsData) return null;

  // Case 1: CD directly in results.results (flat structure)
  if (resultsData.CD != null) {
    return {
      CD: resultsData.CD,
      CL: resultsData.CL,
      L_D: resultsData.L_D || resultsData.L_over_D,
      inlet_velocity: resultsData.inlet_velocity,
      geometry: resultsData.geometry,
      solver: resultsData.solver,
    };
  }

  // Case 2: CD in results.results.results (DLR F6 / Custom Geometry nested structure)
  if (resultsData.results?.CD != null) {
    return {
      CD: resultsData.results.CD,
      CL: resultsData.results.CL,
      L_D: resultsData.results.L_D || resultsData.results.L_over_D,
      inlet_velocity: resultsData.results.inlet_velocity,
      geometry: resultsData.results.geometry,
      solver: resultsData.results.solver || resultsData.solver,
    };
  }

  return null;
};

/**
 * Which results renderer a workflow uses.
 *
 * "optimization" means the run produces the MACH-style S3 artifacts
 * (metadata.json, funcs.json, convergence.png, wing_optimized.stl,
 * wing_fields.vtp) and is served by the mach-wing results endpoint. A
 * manifest-driven case declares this in its ui.results_view, so a new case
 * lands on the right renderer with no change here.
 */
const usesOptimizationResults = (workflowType, cases) => {
  if (workflowType === 'mach_wing') return true;
  const found = findCase(cases, workflowType);
  return found ? (found.ui?.results_view || 'optimization') === 'optimization' : false;
};

const SimulationDetailPage = () => {
  const { t } = useTranslation();
  // Case catalog: drives the results view, the 3D viewers and the cost estimate
  // for manifest-driven cases, so a new case renders correctly without an edit.
  const { cases: catalogCases } = useCaseCatalog();

  const { id } = useParams();
  const navigate = useNavigate();
  const [simulation, setSimulation] = useState(null);
  const [results, setResults] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  // Le serveur retire déjà le diagnostic pour un utilisateur normal ; on
  // n'affiche l'onglet que si la donnée est effectivement arrivée. Le privilège
  // est appliqué en amont, ceci n'est que de la cohérence d'affichage.
  const isSuperadmin = useAuthStore((s) => s.isSuperadmin);
  const diagnostic = results?.results?.metadata?.diagnostic;
  const montrerDiagnostic = Boolean(diagnostic) && isSuperadmin();

  const loadSimulation = useCallback(async () => {
    try {
      setError(null);

      // Load simulation details
      const simData = await simulationService.getSimulation(id);
      setSimulation(simData);

      // Un calcul TERMINÉ, quelle qu'en soit l'issue. La condition ne retenait
      // que « completed », si bien qu'un calcul échoué ou interrompu ne demandait
      // jamais ses résultats — donc jamais son diagnostic, donc pas d'onglet pour
      // le lire. C'était l'inverse du besoin : le calcul qui échoue est celui
      // qu'on a le plus besoin de comprendre, et le conteneur dépose justement
      // son diagnostic dans les deux cas.
      //
      // Bénéfice au passage : un calcul coupé par son plafond de temps affiche
      // enfin la trajectoire qu'il avait déposée avant de s'arrêter. La base ne
      // connaît que quatre statuts — un calcul interrompu y est « failed » ; le
      // mot « interrupted » ne vit que dans le compte rendu du conteneur.
      if (['completed', 'failed'].includes(simData.status)) {
        try {
          let resultsData;
          // Optimization-style runs (MACH Wing and every manifest-driven case
          // that declares ui.results_view = "optimization") write the same S3
          // artifacts, so they share this endpoint and this UI.
          if (usesOptimizationResults(simData.workflow_type, catalogCases)) {
            const response = await api.get(`/api/simulations/mach-wing/results/${id}`);
            resultsData = response.data;
          } else {
            const response = await api.get(`/api/simulations/results/${id}`);
            resultsData = response.data;
          }
          setResults(resultsData);
        } catch (err) {
          // Results API failed - show partial results from simulation data
          // eslint-disable-next-line no-console
          console.warn('Failed to load detailed results:', err);
          // Set minimal results so UI shows something
          setResults({
            results: {
              metadata: {
                duration_seconds: simData.duration_seconds,
                workflow_type: simData.workflow_type,
              },
              optimization: {
                iterations_completed: 0,
                nfunc: 0,
                ngrad: 0,
              }
            },
            images: [],
            files: {}
          });
        }
      }
    } catch (err) {
      setError(t('detail.erreurChargement'));
    } finally {
      setIsLoading(false);
    }
    // catalogCases decides which results endpoint to call, so a reload must
    // follow it: on first render the catalog is still empty.
  }, [id, catalogCases, t]);

  useEffect(() => {
    loadSimulation();
  }, [loadSimulation]);

  // Auto-refresh for running simulations
  useEffect(() => {
    if (!simulation || simulation.status === 'completed' || simulation.status === 'failed') {
      return;
    }

    const interval = setInterval(() => {
      loadSimulation();
    }, 10000); // 10 seconds

    return () => clearInterval(interval);
  }, [simulation, loadSimulation]);

  const handleDelete = async () => {
    if (!window.confirm(t('detail.confirmerSuppression'))) return;

    try {
      await simulationService.deleteSimulation(id);
      navigate('/app/simulations');
    } catch (err) {
      setError(t('detail.erreurSuppression'));
    }
  };

  const handleTerminate = async () => {
    if (!window.confirm('Are you sure you want to terminate this simulation? This will stop the AWS Batch job.')) return;

    try {
      await simulationService.terminateSimulation(id);
      // Reload to show updated status
      loadSimulation();
    } catch (err) {
      setError(t('detail.erreurArret'));
    }
  };

  const formatDate = (isoString) => {
    if (!isoString) return 'N/A';
    return new Date(isoString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (error && !simulation) {
    return (
      <div className="card-dark p-8 text-center">
        <p className="text-error-400 mb-4">{error}</p>
        <Link to="/app/simulations" className="btn-secondary">
          Back to Simulations
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <div className={`${getStatusColorClass(simulation.status)}`}>
              <StatusIcon status={simulation.status} className="w-7 h-7" />
            </div>
            <h1 className="text-2xl font-bold text-dark-100 font-mono">
              {simulation.simulation_id}
            </h1>
          </div>
          <div className="flex items-center gap-4">
            <span
              className="px-3 py-1 rounded-full text-sm font-medium"
              style={{
                backgroundColor: simulationService.getStatusColor(simulation.status) + '20',
                color: simulationService.getStatusColor(simulation.status),
              }}
            >
              {simulation.status}
            </span>
            <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
              simulation.workflow_type === 'mach_wing'
                ? 'bg-accent-500/20 text-accent-400'
                : 'bg-primary-500/20 text-primary-400'
            }`}>
              {simulationService.getWorkflowDisplayName(simulation.workflow_type)}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={loadSimulation}
            className="btn-secondary flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
          {(simulation.status === 'running' || simulation.status === 'queued') && (
            <button
              onClick={handleTerminate}
              className="btn-secondary text-warning-400 hover:bg-warning-500/20 flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 10a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
              </svg>
              Stop
            </button>
          )}
          <button
            onClick={handleDelete}
            className="btn-secondary text-error-400 hover:bg-error-500/20"
          >
            Delete
          </button>
        </div>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="bg-error-500/10 border border-error-500/30 rounded-lg p-4">
          <p className="text-error-400">{error}</p>
        </div>
      )}

      {/* Live progress (timeline) — shown while running and kept as audit
          trail when completed/failed. The timeline component handles its own
          polling and stops once the simulation reaches a terminal state. */}
      <SimulationTimeline
        simulationId={simulation.simulation_id}
        onStatusChange={(s) => {
          if (s && s !== simulation.status) {
            // Refresh the parent page state so result tabs unlock immediately
            loadSimulation();
          }
        }}
      />

      {/* Estimated cost — kept visible while running */}
      {(simulation.status === 'running' || simulation.status === 'queued') && (
        <div className="bg-dark-800/30 border border-dark-700 rounded-xl px-6 py-3 flex items-center justify-between">
          <p className="text-dark-400 text-sm">Estimated cost for this run</p>
          <p className="text-warning-400 font-mono text-lg font-semibold">
            ~${getEstimatedCost(simulation, catalogCases)}
          </p>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-dark-700">
        <nav className="flex gap-6">
          {/* L'onglet « diagnostic » n'était proposé nulle part : son contenu
              existait, l'API le servait, et aucun bouton n'y menait. Le
              diagnostic d'un calcul était donc écrit, déposé et inatteignable. */}
          {[
            ['overview', t('detail.ongletApercu')],
            ['parameters', t('detail.ongletParametres')],
            ['results', t('detail.ongletResultats')],
            ...(montrerDiagnostic ? [['diagnostic', t('detail.ongletDiagnostic')]] : []),
          ].map(([tab, libelle]) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`pb-3 text-sm font-medium transition-colors ${
                activeTab === tab
                  ? 'text-primary-400 border-b-2 border-primary-400'
                  : 'text-dark-400 hover:text-dark-200'
              }`}
            >
              {libelle}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Info Card */}
          <div className="card-dark p-6">
            <h3 className="text-lg font-semibold text-dark-100 mb-4">{t('detail.information')}</h3>
            <div className="space-y-4">
              <InfoRow label={t('detail.identifiant')} value={simulation.simulation_id} mono />
              <InfoRow label="Workflow" value={simulationService.getWorkflowDisplayName(simulation.workflow_type)} />
              <InfoRow label={t('detail.modeAnalyse')} value={simulation.analysis_mode || 'N/A'} />
              <InfoRow label="Status" value={simulation.status} />
              <InfoRow label="Created" value={formatDate(simulation.created_at)} />
              {simulation.started_at && (
                <InfoRow label="Started" value={formatDate(simulation.started_at)} />
              )}
              {simulation.completed_at && (
                <InfoRow label="Completed" value={formatDate(simulation.completed_at)} />
              )}
              <InfoRow
                label="Duration"
                value={simulationService.formatDuration(simulation.duration_seconds)}
              />
            </div>
          </div>

          {/* Quick Stats */}
          <div className="card-dark p-6">
            <h3 className="text-lg font-semibold text-dark-100 mb-4">{t('detail.resume')}</h3>
            {simulation.status === 'completed' && results ? (
              <div className="grid grid-cols-2 gap-4">
                {/* === MACH Wing - Optimization metrics === */}
                {results.results?.optimization && (
                  <>
                    {/* Un calcul direct n'a rien réduit : afficher « Initial CD »
                        et « Final CD » à la même valeur, avec 0 % de réduction,
                        laisserait croire à une optimisation sans effet. Ce sont
                        les coefficients eux-mêmes qui comptent — et le CL n'est
                        visible nulle part ailleurs. */}
                    {results.results.optimization.single_point
                      && Object.keys(results.results.optimization.functions || {}).length > 0 ? (
                      Object.entries(results.results.optimization.functions).map(([nom, valeur]) => (
                        <StatBox key={nom} label={nom} value={Number(valeur).toFixed(5)} />
                      ))
                    ) : (
                      <>
                    <StatBox
                      label={t('detail.iterations')}
                      value={results.results.optimization.iterations_completed || 0}
                    />
                    <StatBox
                      label={t('detail.reductionCD')}
                      value={`${(results.results.optimization.cd_reduction_percent || 0).toFixed(1)}%`}
                    />
                    <StatBox
                      label={t('detail.cdInitial')}
                      value={results.results.optimization.initial_CD?.toFixed(5) || 'N/A'}
                    />
                    <StatBox
                      label={t('detail.cdFinal')}
                      value={results.results.optimization.final_CD?.toFixed(5) || 'N/A'}
                    />
                      </>
                    )}
                    {results.results.optimization.nfunc > 0 && (
                      <StatBox label={t('detail.appelsFonction')} value={results.results.optimization.nfunc} />
                    )}
                    {results.results.optimization.ngrad > 0 && (
                      <StatBox label={t('detail.appelsGradient')} value={results.results.optimization.ngrad} />
                    )}
                  </>
                )}

                {/* === Drone Wing - Alpha sweep optimal performance === */}
                {results.results?.analysis_results?.optimal_performance && (
                  <>
                    <StatBox
                      label="Max L/D Ratio"
                      value={results.results.analysis_results.optimal_performance.max_ld_ratio?.toFixed(2) || 'N/A'}
                    />
                    <StatBox
                      label="Optimal Alpha"
                      value={`${results.results.analysis_results.optimal_performance.alpha_deg?.toFixed(1) || 'N/A'}°`}
                    />
                    <StatBox
                      label="CL at Optimal"
                      value={results.results.analysis_results.optimal_performance.cl?.toFixed(4) || 'N/A'}
                    />
                    <StatBox
                      label="CD at Optimal"
                      value={results.results.analysis_results.optimal_performance.cd?.toFixed(5) || 'N/A'}
                    />
                    {results.results.flow_conditions?.reynolds_number && (
                      <StatBox
                        label="Reynolds"
                        value={results.results.flow_conditions.reynolds_number.toLocaleString()}
                      />
                    )}
                    {results.results.wing_configuration?.aspect_ratio && (
                      <StatBox
                        label="Aspect Ratio"
                        value={results.results.wing_configuration.aspect_ratio.toFixed(2)}
                      />
                    )}
                  </>
                )}

                {/* === CFD Analysis metrics (Custom Geometry, DLR F6) === */}
                {(() => {
                  const cfd = getCfdMetrics(results.results);
                  if (!results.results?.optimization &&
                      !results.results?.analysis_results?.optimal_performance &&
                      cfd) {
                    return (
                      <>
                        <StatBox
                          label="CD (Drag)"
                          value={cfd.CD?.toFixed(5) || 'N/A'}
                        />
                        <StatBox
                          label="CL (Lift)"
                          value={cfd.CL?.toFixed(5) || 'N/A'}
                        />
                        <StatBox
                          label="L/D Ratio"
                          value={cfd.L_D?.toFixed(2) || 'N/A'}
                        />
                        {cfd.inlet_velocity && (
                          <StatBox
                            label="Inlet Velocity"
                            value={`${cfd.inlet_velocity} m/s`}
                          />
                        )}
                        {cfd.geometry && (
                          <StatBox label="Geometry" value={cfd.geometry} />
                        )}
                        {cfd.solver?.iterations && (
                          <StatBox
                            label="Solver Iterations"
                            value={cfd.solver.iterations}
                          />
                        )}
                      </>
                    );
                  }
                  return null;
                })()}

                {/* AWS Cost Display - Always shown */}
                <div className="col-span-2 mt-2 pt-4 border-t border-dark-700">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <svg className={`w-5 h-5 ${results?.cost?.is_estimated === false ? 'text-success-400' : 'text-warning-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="text-dark-400 text-sm">{t('detail.coutAws')}</span>
                      {(!results?.cost || results?.cost?.is_estimated) && (
                        <span className="px-2 py-0.5 text-xs rounded bg-warning-500/20 text-warning-400">
                          Estimated
                        </span>
                      )}
                    </div>
                    <span className={`font-mono text-lg font-semibold ${
                      results?.cost?.is_estimated === false ? 'text-success-400' : 'text-warning-400'
                    }`}>
                      ${results?.cost?.aws_cost_usd != null
                        ? (typeof results.cost.aws_cost_usd === 'number'
                            ? results.cost.aws_cost_usd.toFixed(4)
                            : results.cost.aws_cost_usd)
                        : getEstimatedCost(simulation, catalogCases)}
                    </span>
                  </div>
                  {results?.cost?.instance_type && (
                    <p className="text-dark-500 text-xs mt-1 text-right">
                      {results.cost.instance_type} • {results.cost.compute_hours?.toFixed(2)}h • {results.cost.spot_savings_percent}% spot discount
                    </p>
                  )}
                </div>
                {/* === No data message (fallback) === */}
                {!results.results?.optimization &&
                 !results.results?.analysis_results?.optimal_performance &&
                 !getCfdMetrics(results.results) && (
                  <div className="col-span-2 text-center py-4">
                    <p className="text-dark-400">{t('detail.resultatsIndisponibles')}</p>
                  </div>
                )}
              </div>
            ) : simulation.status === 'failed' ? (
              <div className="space-y-4">
                <div className="bg-error-500/10 rounded-lg p-4">
                  <p className="text-error-400">{simulation.error_message || 'Simulation failed'}</p>
                </div>
                {/* Show estimated cost even for failed simulations */}
                <div className="pt-4 border-t border-dark-700">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <svg className="w-5 h-5 text-warning-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="text-dark-400 text-sm">AWS Cost</span>
                      <span className="px-2 py-0.5 text-xs rounded bg-warning-500/20 text-warning-400">
                        Estimated
                      </span>
                    </div>
                    <span className="text-warning-400 font-mono text-lg font-semibold">
                      ${getEstimatedCost(simulation, catalogCases)}
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="text-center py-4">
                  <p className="text-dark-400">{t('detail.resultatsApres')}</p>
                </div>
                {/* Show estimated cost for running/queued simulations */}
                <div className="pt-4 border-t border-dark-700">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <svg className="w-5 h-5 text-warning-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="text-dark-400 text-sm">AWS Cost</span>
                      <span className="px-2 py-0.5 text-xs rounded bg-warning-500/20 text-warning-400">
                        Estimated
                      </span>
                    </div>
                    <span className="text-warning-400 font-mono text-lg font-semibold">
                      ${getEstimatedCost(simulation, catalogCases)}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Hors des trois branches ci-dessus : les journaux valent pour un
                calcul réussi autant que raté. Sur un échec ils disent ce qui
                s'est passé ; sur un succès ils portent les résidus et les
                valeurs, que l'équipe simulation ne peut lire nulle part
                ailleurs sans accès à la console AWS. */}
            {simulation.batch_job_id !== null && (
              <JournauxCalcul
                simulationId={simulation.simulation_id}
                statut={simulation.status}
              />
            )}
          </div>
        </div>
      )}

      {activeTab === 'diagnostic' && montrerDiagnostic && (
        <div className="card-dark p-6">
          <DiagnosticSimulation diagnostic={diagnostic} />
        </div>
      )}

      {activeTab === 'parameters' && (
        <div className="card-dark p-6">
          <h3 className="text-lg font-semibold text-dark-100 mb-4">{t('detail.parametres')}</h3>
          {simulation.parameters && Object.keys(simulation.parameters).length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {Object.entries(simulation.parameters).map(([key, value]) => (
                <div key={key} className="bg-dark-900 rounded-lg p-4">
                  <p className="text-dark-500 text-xs uppercase tracking-wider mb-1">
                    {key.replace(/_/g, ' ')}
                  </p>
                  <p className="text-dark-100 font-mono text-lg">
                    {typeof value === 'number' ? value.toFixed(4) : String(value)}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-dark-400">{t('detail.aucunParametre')}</p>
          )}
        </div>
      )}

      {activeTab === 'results' && (
        <div className="space-y-6">
          {simulation.status !== 'completed' ? (
            <div className="card-dark p-8 text-center">
              <svg
                className="w-16 h-16 text-dark-600 mx-auto mb-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <h3 className="text-lg font-medium text-dark-300 mb-2">
                {simulation.status === 'failed' ? t('detail.echec') : t('detail.attenteResultats')}
              </h3>
              <p className="text-dark-500">
                {simulation.status === 'failed'
                  ? simulation.error_message || 'An error occurred during simulation'
                  : 'Results will be available once the simulation completes'}
              </p>
            </div>
          ) : results ? (
            <>
              {/* 3D Wing Visualization - Geometry (STL) */}
              {usesOptimizationResults(simulation.workflow_type, catalogCases) && results.files?.wing_surface_stl && (
                <div className="card-dark p-6">
                  <h3 className="text-lg font-semibold text-dark-100 mb-4">3D Wing Geometry</h3>
                  <Suspense fallback={
                    <div className="bg-gray-800 rounded-lg p-8 flex items-center justify-center" style={{ height: '400px' }}>
                      <div className="text-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-3"></div>
                        <p className="text-gray-400 text-sm">{t('detail.chargementVisualiseur3D')}</p>
                      </div>
                    </div>
                  }>
                    <WingViewer3D
                      stlUrl={results.files.wing_surface_stl}
                      title="Optimized Wing Surface"
                    />
                  </Suspense>
                </div>
              )}

              {/* CFD Field Visualization (VTK with colormaps) */}
              {usesOptimizationResults(simulation.workflow_type, catalogCases) && results.files?.wing_fields_vtp && (
                <div className="card-dark p-6">
                  <h3 className="text-lg font-semibold text-dark-100 mb-4">{t('detail.champsCFD')}</h3>
                  <Suspense fallback={
                    <div className="bg-gray-800 rounded-lg p-8 flex items-center justify-center" style={{ height: '450px' }}>
                      <div className="text-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-3"></div>
                        <p className="text-gray-400 text-sm">{t('detail.chargementVisualiseurCFD')}</p>
                      </div>
                    </div>
                  }>
                    <WingViewerVTK
                      vtpUrl={results.files.wing_fields_vtp}
                      title={t('detail.champsPressionVitesse')}
                    />
                  </Suspense>
                </div>
              )}

              {/* 3D Visualizations from images array (for DLR and other workflows) */}
              {results.images && results.images.some(img => img.type === 'stl') && (
                <div className="card-dark p-6">
                  <h3 className="text-lg font-semibold text-dark-100 mb-4">{t('detail.geometrie3D')}</h3>
                  {results.images.filter(img => img.type === 'stl').map((file, idx) => (
                    <Suspense key={idx} fallback={
                      <div className="bg-gray-800 rounded-lg p-8 flex items-center justify-center" style={{ height: '400px' }}>
                        <div className="text-center">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-3"></div>
                          <p className="text-gray-400 text-sm">Loading 3D viewer...</p>
                        </div>
                      </div>
                    }>
                      <WingViewer3D
                        stlUrl={file.url}
                        title={file.name}
                      />
                    </Suspense>
                  ))}
                </div>
              )}

              {/* VTP Field Visualization from images array */}
              {results.images && results.images.some(img => img.type === 'vtp') && (
                <div className="card-dark p-6">
                  <h3 className="text-lg font-semibold text-dark-100 mb-4">CFD Field Analysis</h3>
                  {results.images.filter(img => img.type === 'vtp').map((file, idx) => (
                    <Suspense key={idx} fallback={
                      <div className="bg-gray-800 rounded-lg p-8 flex items-center justify-center" style={{ height: '450px' }}>
                        <div className="text-center">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-3"></div>
                          <p className="text-gray-400 text-sm">Loading CFD viewer...</p>
                        </div>
                      </div>
                    }>
                      <WingViewerVTK
                        vtpUrl={file.url}
                        title={file.name}
                      />
                    </Suspense>
                  ))}
                </div>
              )}

              {/* Images/Visualizations (PNG only) */}
              {results.images && results.images.filter(img => img.type === 'static' || !img.type).length > 0 && (
                <div className="card-dark p-6">
                  <h3 className="text-lg font-semibold text-dark-100 mb-4">{t('detail.convergence')}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {results.images.filter(img => img.type === 'static' || !img.type).map((img, idx) => (
                      <div key={idx} className="bg-dark-900 rounded-lg p-2">
                        <img
                          src={img.url}
                          alt={img.name}
                          className="w-full rounded"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                        <p className="text-dark-400 text-sm text-center mt-2">{img.name}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Files */}
              {results.files && Object.keys(results.files).length > 0 && (
                <div className="card-dark p-6">
                  <h3 className="text-lg font-semibold text-dark-100 mb-4">{t('detail.fichiers')}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {Object.entries(results.files).map(([key, url]) => (
                      url && (
                        <a
                          key={key}
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-3 p-3 bg-dark-900 rounded-lg hover:bg-dark-700 transition-colors"
                        >
                          <svg className="w-5 h-5 text-dark-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          <span className="text-dark-200 text-sm">
                            {key.replace(/_/g, ' ')}
                          </span>
                        </a>
                      )
                    ))}
                  </div>
                </div>
              )}

              {/* Optimization Details */}
              {results.results?.optimization && (
                <div className="card-dark p-6">
                  <h3 className="text-lg font-semibold text-dark-100 mb-4">{t('detail.resultatsOptimisation')}</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <StatBox
                      label="Function Calls"
                      value={results.results.optimization.nfunc || 0}
                    />
                    <StatBox
                      label="Gradient Calls"
                      value={results.results.optimization.ngrad || 0}
                    />
                    <StatBox
                      label="Initial CD"
                      value={results.results.optimization.initial_CD?.toExponential(4) || 'N/A'}
                    />
                    <StatBox
                      label="Final CD"
                      value={results.results.optimization.final_CD?.toExponential(4) || 'N/A'}
                    />
                  </div>
                </div>
              )}

              {/* AWS Cost Details - Always shown */}
              <div className="card-dark p-6">
                <h3 className="text-lg font-semibold text-dark-100 mb-4 flex items-center gap-2">
                  <svg className={`w-5 h-5 ${results?.cost?.is_estimated === false ? 'text-success-400' : 'text-warning-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  AWS Compute Cost
                  {(!results?.cost || results?.cost?.is_estimated) && (
                    <span className="px-2 py-0.5 text-xs rounded bg-warning-500/20 text-warning-400">
                      Estimated
                    </span>
                  )}
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-dark-900 rounded-lg p-4 text-center">
                    <p className="text-dark-500 text-xs uppercase tracking-wider mb-1">{t('detail.coutTotal')}</p>
                    <p className={`font-mono text-xl font-semibold ${
                      results?.cost?.is_estimated === false ? 'text-success-400' : 'text-warning-400'
                    }`}>
                      ${results?.cost?.aws_cost_usd != null
                        ? (typeof results.cost.aws_cost_usd === 'number'
                            ? results.cost.aws_cost_usd.toFixed(4)
                            : results.cost.aws_cost_usd)
                        : getEstimatedCost(simulation, catalogCases)}
                    </p>
                  </div>
                  <StatBox
                    label={t('detail.typeMachine')}
                    value={results?.cost?.instance_type || 'N/A'}
                  />
                  <StatBox
                    label={t('detail.heuresCalcul')}
                    value={results?.cost?.compute_hours?.toFixed(4) || 'N/A'}
                  />
                  <StatBox
                    label={t('detail.remiseSpot')}
                    value={`${results?.cost?.spot_savings_percent || 70}%`}
                  />
                </div>
                <p className="text-dark-500 text-xs mt-4">
                  {results?.cost?.is_estimated
                    ? 'Estimated cost based on typical workflow duration. Actual cost will be calculated after completion.'
                    : 'Cost calculated using EC2 on-demand pricing with spot discount. Actual AWS billing may vary slightly.'}
                </p>
              </div>

              {/* Custom Geometry CFD Results */}
              {simulation.workflow_type === 'custom_geometry' && results.results && (
                <div className="card-dark p-6">
                  <h3 className="text-lg font-semibold text-dark-100 mb-4 flex items-center gap-2">
                    <svg className="w-5 h-5 text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                    CFD Analysis Results
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-dark-900 rounded-lg p-4 text-center">
                      <p className="text-dark-500 text-xs uppercase tracking-wider mb-1">Drag Coefficient (CD)</p>
                      <p className="text-primary-400 font-mono text-2xl font-semibold">
                        {typeof results.results.CD === 'number' ? results.results.CD.toFixed(4) : results.results.CD || 'N/A'}
                      </p>
                    </div>
                    <div className="bg-dark-900 rounded-lg p-4 text-center">
                      <p className="text-dark-500 text-xs uppercase tracking-wider mb-1">Lift Coefficient (CL)</p>
                      <p className="text-primary-400 font-mono text-2xl font-semibold">
                        {typeof results.results.CL === 'number' ? results.results.CL.toFixed(4) : results.results.CL || 'N/A'}
                      </p>
                    </div>
                    <div className="bg-dark-900 rounded-lg p-4 text-center">
                      <p className="text-dark-500 text-xs uppercase tracking-wider mb-1">Lift-to-Drag Ratio (L/D)</p>
                      <p className="text-primary-400 font-mono text-2xl font-semibold">
                        {typeof results.results.L_D === 'number' ? results.results.L_D.toFixed(4) : results.results.L_D || 'N/A'}
                      </p>
                    </div>
                  </div>
                  {results.results.geometry && (
                    <div className="mt-4 pt-4 border-t border-dark-700">
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-dark-500">Geometry:</span>
                          <span className="text-dark-200 ml-2">{results.results.geometry}</span>
                        </div>
                        <div>
                          <span className="text-dark-500">Inlet Velocity:</span>
                          <span className="text-dark-200 ml-2">{results.results.inlet_velocity} m/s</span>
                        </div>
                        {results.results.duration_seconds && (
                          <div>
                            <span className="text-dark-500">Simulation Time:</span>
                            <span className="text-dark-200 ml-2">{(results.results.duration_seconds / 60).toFixed(1)} min</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* No detailed results message */}
              {simulation.workflow_type !== 'custom_geometry' &&
               (!results.images || results.images.length === 0) &&
               (!results.files || Object.keys(results.files).filter(k => results.files[k]).length === 0) &&
               !results.results?.optimization?.initial_CD && (
                <div className="card-dark p-6 text-center">
                  <svg className="w-12 h-12 text-dark-500 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <p className="text-dark-400">
                    Detailed results are not available for this simulation.
                  </p>
                  <p className="text-dark-500 text-sm mt-2">
                    The optimization completed quickly (possibly converged in 1 iteration).
                  </p>
                </div>
              )}
            </>
          ) : (
            <div className="card-dark p-8 text-center">
              <p className="text-dark-400">{t('detail.chargementResultats')}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Helper Components
const InfoRow = ({ label, value, mono }) => (
  <div className="flex justify-between items-center py-2 border-b border-dark-700 last:border-0">
    <span className="text-dark-400">{label}</span>
    <span className={`text-dark-100 ${mono ? 'font-mono text-sm' : ''}`}>{value}</span>
  </div>
);

const StatBox = ({ label, value }) => (
  <div className="bg-dark-900 rounded-lg p-4 text-center">
    <p className="text-dark-500 text-xs uppercase tracking-wider mb-1">{label}</p>
    <p className="text-dark-100 font-mono text-xl">{value}</p>
  </div>
);

export default SimulationDetailPage;
